/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useMemo, useEffect } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Text from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import { DOCUMENT_TEMPLATES } from '../../constants';
import Select from '../common/Select';
import { useUserActions, useUserQuery } from '../../actions/UserActions';
import { useDocumentsActions } from '../../actions/DocumentActions';
import CreateDocumentPreview from './CreateDocumentPreview';
import ModelSelector from '../thread/ModelSelector';
import { track } from '../../utils/track';

import Switch from '../common/Switch';

import CreateMeetingMinutesDocument from './CreateMeetingMinutesDocument';

function getTemplateName(template, t) {
  if (template === 'interview') {
    return t('Intervju');
  }
  if (template === 'meeting') {
    return t('Mötesprotokoll');
  }
  if (template === 'summary') {
    return t('Sammanfattning');
  }
  if (template === 'empty') {
    return t('Nytt dokument');
  }
  return null;
}

function CreatePredined({ data, onClose, switchOptions, onSwitchChange }) {
  const { t } = useTranslation();
  const { updateUserAsync } = useUserActions();
  const { createDocument } = useDocumentsActions(data?.fileId);
  const [changeDefault, setChangeDefault] = useState(false);
  const [template, setTemplate] = useState(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);

  const { user } = useUserQuery();
  const workspace = user?.workspace;

  const templates = useMemo(() => {
    return DOCUMENT_TEMPLATES.concat(
      workspace?.templates
        ? workspace.templates.map((x) => ({
            value: x.id,
            label: x.name,
            free: true,
          }))
        : []
    );
  }, [workspace]);

  useEffect(() => {
    if (templates.length > 0 && !template) {
      setTemplate(user?.default_template || 'summary');
    }
  }, [templates, template, user]);

  async function onConfirm() {
    try {
      setLoading(true);
      let isOrgTemplate = workspace.templates.find((x) => x.id === template);

      if (changeDefault && template !== 'empty') {
        track('document:setDefaultTemplate', {
          template,
          isOrgTemplate: !!isOrgTemplate,
        });
        await updateUserAsync({
          defaultTemplate: template,
        });
      }

      track('document:create', {
        template: template === 'empty' ? null : template,
        isOrgTemplate: !!isOrgTemplate,
        model: user?.default_model,
      });

      const res = await createDocument({
        template: template === 'empty' ? null : template,
        status: template === 'empty' ? 'ok' : undefined,
        title: title
          ? title
          : isOrgTemplate
          ? isOrgTemplate.name
          : getTemplateName(template, t),
      });

      setLoading(false);
      onClose(res?.id);
    } catch (err) {
      setLoading(false);
    }
  }

  const isPaying =
    user?.workspace?.type === 'stripe' ||
    user?.workspace?.type === 'enterprise';

  const templateOptions = useMemo(() => {
    return templates.map((x) => {
      let icon;
      let disabled = false;
      if (!isPaying && !x.free) {
        disabled = true;
        icon = (
          <Chip
            label={t('Pro')}
            size="small"
            color="primary"
            css={css`
              margin-right: 0.5rem;
            `}
          />
        );
      }
      return {
        ...x,
        disabled,
        icon,
      };
    });
  }, [isPaying, templates, t]);

  return (
    <>
      <DialogContent>
        <div
          css={css`
            display: flex;
            gap: 2rem;
            height: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              max-width: 254px;
              gap: 1rem;
            `}
          >
            {workspace.meeting_minutes && (
              <Switch
                options={switchOptions}
                value="templates"
                variant="outlined"
                onChange={() => onSwitchChange()}
              />
            )}
            <Text variant="h4" fontWeight="bold">
              {t('Skapa nytt dokument')}
            </Text>
            <Text variant="body1" color="textSecondary">
              {t(
                'Skapa ett nytt dokument från en av de fördefinierade mallarna.'
              )}
            </Text>
            <TextField
              value={title}
              placeholder={getTemplateName(template, t)}
              onChange={(e) => setTitle(e.target.value)}
              label={t('Namnge ditt dokument')}
              InputProps={{
                sx: {
                  backgroundColor: '#fff',
                },
              }}
            />
            <Select
              MenuProps={{
                style: {
                  zIndex: 35001,
                },
              }}
              options={templateOptions}
              value={template || ''}
              variant="outlined"
              css={css`
                background-color: #fff;
              `}
              onChange={(e) => {
                setTemplate(e.target.value);
              }}
              label={t('Dokumenttyp')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={changeDefault}
                  onChange={(e) => setChangeDefault(e.target.checked)}
                />
              }
              label={t('Spara som standardmall')}
            />
          </div>
          <CreateDocumentPreview template={template} meeting={data?.meeting} />
        </div>
      </DialogContent>
      <div
        css={css`
          display: flex;
          gap: 1rem;
          padding: 1rem;
          justify-content: space-between;
        `}
      >
        <ModelSelector />
        <div>
          <Button onClick={() => onClose(false)}>{t('Avbryt')}</Button>
          <Button
            onClick={onConfirm}
            disabled={!template || loading}
            variant="contained"
            color="primary"
          >
            {t('Skapa dokument')}
            {loading ? <CircularProgress size={16} /> : ''}
          </Button>
        </div>
      </div>
    </>
  );
}

export default function CreateDocumentDialog({ open, data, onClose }) {
  const [tab, setTab] = useState('templates');
  const { t } = useTranslation();

  const switchOptions = useMemo(() => {
    return [
      {
        value: 'templates',
        label: t('Mallar'),
      },
      { value: 'minutes', label: t('Mötesprotokoll') },
    ];
  }, [t]);

  return (
    <MuiDialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={() => onClose()}
      PaperProps={{
        sx: {
          backgroundColor: '#ECF3FF',
          minHeight: 470,
          borderRadius: 4, // 16px
        },
      }}
    >
      {tab === 'minutes' ? (
        <CreateMeetingMinutesDocument
          data={data}
          onClose={onClose}
          switchOptions={switchOptions}
          onSwitchChange={() => setTab('templates')}
        />
      ) : (
        <CreatePredined
          data={data}
          onClose={onClose}
          switchOptions={switchOptions}
          onSwitchChange={() => setTab('minutes')}
        />
      )}
    </MuiDialog>
  );
}
